<template>
  <div class="flex flex-col gap-4">
    <div class="grid grid-cols-2 gap-2">
      <div class="flex flex-col gap-2">
        <div class="flex items-center justify-between">
          <span class="color_666 text-[13px]">Email Signature</span>
          <div class="flex items-center">
            <a-tooltip
              :title="
                hasUnsupportedTags
                  ? 'Your signature has some HTML elements (table tags) that aren\'t supported in the simple text editor. Please use the HTML editor only to edit your signature as HTML code.'
                  : ''
              "
              color="var(--tooltip-bg)"
            >
              <div
                class="signature_toggle_btn"
                :class="{
                  active: !isHtmlMode,
                  'cursor-pointer': !hasUnsupportedTags,
                  'cursor-not-allowed opacity-50': hasUnsupportedTags,
                }"
                @click="!hasUnsupportedTags && toggleMode(false)"
              >
                <ThemeImage
                  :icon="
                    !isHtmlMode
                      ? imageNames.TEXT_EDITOR
                      : imageNames.TEXT_EDITOR_GREY
                  "
                  alt="rich_text_icon"
                  class="size-4"
                />
              </div>
            </a-tooltip>
            <div
              class="signature_toggle_btn cursor-pointer"
              :class="{ active: isHtmlMode }"
              @click="toggleMode(true)"
              title="HTML"
            >
              <ThemeImage
                :icon="
                  isHtmlMode ? imageNames.HTML_CODE : imageNames.HTML_CODE_GREY
                "
                alt="html_icon"
                class="size-4"
              />
            </div>
          </div>
        </div>
        <div>
          <div
            v-if="!isHtmlMode && !hasUnsupportedTags"
            class="card_container bg_white_4"
          >
            <QuillEditor
              v-model:modelValue="internalValue"
              ref="quillEditorRef"
              :height="quillHeight"
            />
          </div>
          <HtmlCodeEditor
            v-else
            v-model="internalValue"
            :height="htmlHeight"
            placeholder="Paste HTML here"
          />
        </div>
      </div>
      <div class="flex flex-col gap-2">
        <span class="color_666 text-[13px] py-1">Preview</span>
        <div
          class="card_container bg_white_4 overflow-hidden"
          :style="{ height: `${previewHeight}px` }"
        >
          <SignatureIframeRenderer
            v-if="hasUnsupportedTags"
            :content="internalValue"
            :height="previewHeight - 6"
          />
          <QuillEditorContentRenderer
            v-else
            class="text-[12px] h-full overflow-y-auto p-3"
            source="signature_preview"
            :content="internalValue"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick } from "vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { quillUnsupportedTags } from "@/src/constants/emailConstants.js";
import QuillEditor from "@/src/components/quill_editor/QuillEditor.vue";
import HtmlCodeEditor from "@/src/components/editor_components/html_editor/HtmlCodeEditor.vue";
import SignatureIframeRenderer from "@/src/components/signature_editor/SignatureIframeRenderer.vue";
import QuillEditorContentRenderer from "@/src/components/quill_editor/QuillEditorContentRenderer.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";

const props = defineProps({
  modelValue: {
    type: String,
    default: "",
  },
  height: {
    type: Number,
    default: 300,
  },
});

const emit = defineEmits(["update:modelValue", "mode-change"]);

const internalValue = ref("");
const isHtmlMode = ref(false);
const hasUnsupportedTags = ref(false);
const quillEditorRef = ref(null);

const quillHeight = computed(() => {
  return props.height - 40;
});

const htmlHeight = computed(() => {
  return props.height;
});

const previewHeight = computed(() => {
  return props.height;
});

const checkForUnsupportedTags = (htmlContent) => {
  if (!htmlContent) return false;
  for (const tag of quillUnsupportedTags) {
    const tagPattern = new RegExp(`<\\s*${tag}(\\s|>)`, "i");
    if (tagPattern.test(htmlContent)) {
      return true;
    }
  }
  return false;
};

hasUnsupportedTags.value = checkForUnsupportedTags(
  (typeof props.modelValue === "string" ? props.modelValue : "") || "",
);
if (hasUnsupportedTags.value) {
  isHtmlMode.value = true;
}

const updateUnsupportedTagsStatus = () => {
  hasUnsupportedTags.value = checkForUnsupportedTags(internalValue.value);
  if (hasUnsupportedTags.value && !isHtmlMode.value) {
    isHtmlMode.value = true;
    emit("mode-change", "html");
  }
};

const toggleMode = (htmlMode) => {
  isHtmlMode.value = htmlMode;
  emit("mode-change", htmlMode ? "html" : "quill");
};

watch(
  () => props.modelValue,
  (newValue) => {
    internalValue.value = newValue || "";
  },
  { immediate: true },
);

watch(internalValue, (newValue) => {
  emit("update:modelValue", newValue);
  updateUnsupportedTagsStatus();
});

onMounted(() => {
  nextTick(() => {
    updateUnsupportedTagsStatus();
  });
});
</script>

<style scoped>
.signature_toggle_btn {
  display: flex;
  width: 26px;
  height: 26px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
}

.signature_toggle_btn:hover,
.signature_toggle_btn.active {
  background: var(--white-opacity-8);
}
</style>
